import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DocumentMimeKind } from '../types';

const ZOOM_STEP = 0.15;
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 3;
const DBL_ZOOM = 1.75;

export function useViewport(args: {
    containerRef: React.RefObject<HTMLDivElement>;
    loading: boolean;
    error: string | null;
    mimeKind: DocumentMimeKind;
    fitImageToStage: () => void;
}) {
    const { containerRef, loading, error, mimeKind, fitImageToStage } = args;
    const [scale, setScale] = useState(1);
    const [fitScale, setFitScale] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const scaleRef = useRef(1);
    const panRef = useRef({ x: 0, y: 0 });
    const lastFitRef = useRef(0);
    const lastBoxRef = useRef({ w: 0, h: 0 });
    const dragRef = useRef<{ active: boolean; startX: number; startY: number; origX: number; origY: number }>({
        active: false, startX: 0, startY: 0, origX: 0, origY: 0
    });

    const setPanningClass = (active: boolean) => {
        const el = containerRef.current;

        if (!el) {
            return;
        }

        if (active) {
            el.classList.add('is-panning');
            return;
        }

        el.classList.remove('is-panning');
    };

    useEffect(() => {
        panRef.current = pan;
    }, [pan]);

    useEffect(() => {
        scaleRef.current = scale;
    }, [scale]);

    const reset = useCallback(() => {
        setScale(1);
        setFitScale(1);
        lastFitRef.current = 0;
        lastBoxRef.current = { w: 0, h: 0 };
        panRef.current = { x: 0, y: 0 };
        setPan({ x: 0, y: 0 });
    }, []);

    const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, +value.toFixed(2)));

    const zoomAtPoint = useCallback((clientX: number, clientY: number, nextScale: number) => {
        const container = containerRef.current;
        const target = clampZoom(nextScale);
        const current = scaleRef.current;
        if (!container || Math.abs(target - current) < 0.001) {
            return;
        }
        const rect = container.getBoundingClientRect();
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        const ox = clientX - rect.left - cx;
        const oy = clientY - rect.top - cy;
        const factor = target / current;
        const nextPan = {
            x: ox - (ox - panRef.current.x) * factor,
            y: oy - (oy - panRef.current.y) * factor
        };
        panRef.current = nextPan;
        setPan(nextPan);
        scaleRef.current = target;
        setScale(target);
    }, [containerRef]);

    const zoomIn = () => {
        const container = containerRef.current;
        if (!container) {
            const next = clampZoom(scaleRef.current + ZOOM_STEP);
            scaleRef.current = next;
            setScale(next);
            return;
        }
        const rect = container.getBoundingClientRect();
        zoomAtPoint(rect.left + rect.width / 2, rect.top + rect.height / 2, scaleRef.current + ZOOM_STEP);
    };

    const zoomOut = () => {
        const container = containerRef.current;
        if (!container) {
            const next = clampZoom(scaleRef.current - ZOOM_STEP);
            scaleRef.current = next;
            setScale(next);
            return;
        }
        const rect = container.getBoundingClientRect();
        zoomAtPoint(rect.left + rect.width / 2, rect.top + rect.height / 2, scaleRef.current - ZOOM_STEP);
    };

    const zoomReset = () => {
        scaleRef.current = 1;
        setScale(1);
        panRef.current = { x: 0, y: 0 };
        setPan({ x: 0, y: 0 });
        lastFitRef.current = 0;
        window.setTimeout(() => fitImageToStage(), 0);
    };

    const panHitIntoView = useCallback((els: ArrayLike<Element>) => {
        const container = containerRef.current;

        if (!container || !els.length) {
            return false;
        }

        let left = Infinity;
        let top = Infinity;
        let right = -Infinity;
        let bottom = -Infinity;

        for (let i = 0; i < els.length; i++) {
            const r = els[i].getBoundingClientRect();

            if (r.width <= 0 && r.height <= 0) {
                continue;
            }

            left = Math.min(left, r.left);
            top = Math.min(top, r.top);
            right = Math.max(right, r.right);
            bottom = Math.max(bottom, r.bottom);
        }

        if (!isFinite(left) || right <= left || bottom <= top) {
            return false;
        }

        if (scaleRef.current <= 1.01) {
            if (Math.abs(panRef.current.x) > 0.5 || Math.abs(panRef.current.y) > 0.5) {
                panRef.current = { x: 0, y: 0 };
                setPan({ x: 0, y: 0 });
            }

            return true;
        }

        const cRect = container.getBoundingClientRect();
        const dx = (cRect.left + cRect.right) / 2 - (left + right) / 2;
        const dy = (cRect.top + cRect.bottom) / 2 - (top + bottom) / 2;

        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
            return true;
        }

        const next = {
            x: panRef.current.x + dx,
            y: panRef.current.y + dy
        };
        panRef.current = next;
        setPan(next);
        return true;
    }, [containerRef]);

    const canPan = scale > 1.01;
    const viewAway = scale > 1.01 || Math.abs(pan.x) > 2 || Math.abs(pan.y) > 2;

    const endPan = () => {
        if (!dragRef.current.active) {
            return;
        }

        dragRef.current.active = false;
        setPanningClass(false);
    };

    const onPointerDown = (e: React.PointerEvent) => {
        if (containerRef.current && e.button === 0) {
            try { containerRef.current.focus(); } catch { }
        }

        if (!canPan || e.button !== 0) {
            return;
        }

        e.preventDefault();
        dragRef.current = {
            active: true,
            startX: e.clientX,
            startY: e.clientY,
            origX: panRef.current.x,
            origY: panRef.current.y
        };
        setPanningClass(true);

        try {
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        } catch { }
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!dragRef.current.active) {
            return;
        }

        const next = {
            x: dragRef.current.origX + (e.clientX - dragRef.current.startX),
            y: dragRef.current.origY + (e.clientY - dragRef.current.startY)
        };
        panRef.current = next;
        setPan(next);
    };

    const onPointerUp = () => {
        endPan();
    };

    const onStageDoubleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (scaleRef.current <= 1.05) {
            zoomAtPoint(e.clientX, e.clientY, DBL_ZOOM);
            return;
        }
        zoomReset();
    };

    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        const onWheel = (e: WheelEvent) => {
            if (loading || error || (mimeKind !== 'pdf' && mimeKind !== 'image')) {
                return;
            }
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const direction = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
                zoomAtPoint(e.clientX, e.clientY, scaleRef.current + direction);
                return;
            }
            if (scaleRef.current > 1.01) {
                e.preventDefault();
                const unit = e.deltaMode === 1 ? 16 : (e.deltaMode === 2 ? 80 : 1);
                let dx = e.deltaX * unit;
                let dy = e.deltaY * unit;

                if (e.shiftKey) {
                    dx = Math.abs(dx) > 0.5 ? dx : dy;
                    dy = 0;
                }

                const next = {
                    x: panRef.current.x - dx,
                    y: panRef.current.y - dy
                };
                panRef.current = next;
                setPan(next);
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [loading, error, mimeKind, zoomAtPoint, containerRef]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (loading || error || (mimeKind !== 'pdf' && mimeKind !== 'image')) {
                return;
            }
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
                return;
            }
            if (e.key === '+' || e.key === '=') {
                e.preventDefault();
                zoomIn();
                return;
            }
            if (e.key === '-' || e.key === '_') {
                e.preventDefault();
                zoomOut();
                return;
            }
            if (e.key === '0') {
                e.preventDefault();
                zoomReset();
                return;
            }
            if (!canPan && scaleRef.current <= 1.01) {
                return;
            }
            const step = e.shiftKey ? 80 : 40;
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                const next = { x: panRef.current.x + step, y: panRef.current.y };
                panRef.current = next;
                setPan(next);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                const next = { x: panRef.current.x - step, y: panRef.current.y };
                panRef.current = next;
                setPan(next);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                const next = { x: panRef.current.x, y: panRef.current.y + step };
                panRef.current = next;
                setPan(next);
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                const next = { x: panRef.current.x, y: panRef.current.y - step };
                panRef.current = next;
                setPan(next);
            }
        };
        el.addEventListener('keydown', onKeyDown);
        return () => el.removeEventListener('keydown', onKeyDown);
    }, [loading, error, mimeKind, canPan, zoomAtPoint, containerRef]);

    useEffect(() => {
        const onMove = (e: PointerEvent) => {
            if (!dragRef.current.active) {
                return;
            }

            const next = {
                x: dragRef.current.origX + (e.clientX - dragRef.current.startX),
                y: dragRef.current.origY + (e.clientY - dragRef.current.startY)
            };
            panRef.current = next;
            setPan(next);
        };
        const onUp = () => {
            endPan();
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);

        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
        };
    }, []);

    return {
        scale,
        setScale,
        fitScale,
        setFitScale,
        pan,
        setPan,
        scaleRef,
        panRef,
        lastFitRef,
        lastBoxRef,
        zoomIn,
        zoomOut,
        zoomReset,
        canPan,
        viewAway,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onStageDoubleClick,
        panHitIntoView,
        reset
    };
}
